import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import React, { useEffect, useState } from "react";
import { X, ChevronLeft } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../state-management/root-reducer";
import ADMIN_PANEL from "../admin/AdminPanel.component";
import OUTAGE_FORM from "../form/submit-outage/OutageForm.component";
import OUTAGE_SEARCH from "../form/outage-search/OutageSearch.component";
import { closeDrawer, openDrawer } from "./Drawer.animations";
import DrawerStyles from "./Drawer.styles";
import { DefaultDrawerProps, IDrawerProps } from "./Drawer.types";
import {
  clearSearchedOutages,
  ISubmittedOutage,
} from "../../state-management/slices/outages/outages.slice";
import { IGTMEventFunction } from "../../shared/types/logging.types";
import { ESearchWizardSteps } from "../../shared/enums/search.enums";
import { AppDispatch } from "../../state-management/store";
import {
  clearSearchTerm,
  IAdminModeState,
  IAuthState,
  ISearchModeState,
  updateSearchView,
} from "../../state-management/slices/global/global.slice";
import { ClickEvent } from "../../shared/types/event.types";
import CancelIcon from "../../theme/assets/drawer-controls/cancel.svg";
import BackIcon from "../../theme/assets/drawer-controls/back.svg";

const renderTitleContainer = (
  submittedOutage: ISubmittedOutage,
  searchMode: ISearchModeState,
  adminMode: IAdminModeState,
  auth: IAuthState
) => {
  let title = "";
  let titleClassName = "";
  if (searchMode.enabled) {
    title = "Check Outage Status";
    if (searchMode.activeView === ESearchWizardSteps.RESULTS) {
      titleClassName = "search-mode";
    }
  } else if (auth.loggedIn && adminMode.enabled) {
    title = "Admin Panel";
  } else {
    title = submittedOutage.isSubmitted
      ? "Report Complete"
      : "Report an Outage";
  }

  return (
    <div className="title-container">
      <h1 className={titleClassName}>{title}</h1>
    </div>
  );
};

const renderCloseButtonContainer = (
  submittedOutage: ISubmittedOutage,
  auth: IAuthState,
  sendDataToGTM: IGTMEventFunction,
  onClose: (event: ClickEvent) => void
) => {
  return (
    <div className="close-button-container">
      <button
        onClick={(event) => {
          const eventLabel = submittedOutage.isSubmitted
            ? "Close button clicked from submit confirmation screen"
            : "Cancel button clicked from outage form";

          sendDataToGTM({
            event: "eventTracking",
            category: "Outage",
            action: "Street Light",
            label: eventLabel,
          });

          onClose(event);
        }}
      >
        <img src={CancelIcon} alt="Cancel" />
        <span className="close-button-label">
          {submittedOutage.isSubmitted || auth.loggedIn ? "CLOSE" : "CANCEL"}
        </span>
      </button>
    </div>
  );
};

const renderBackButtonContainer = (dispatch: AppDispatch) => {
  return (
    <div className="back-button-container">
      <button
        onClick={(event: ClickEvent) => {
          event.preventDefault();
          dispatch(updateSearchView(ESearchWizardSteps.SEARCH));
          dispatch(clearSearchTerm());
          dispatch(clearSearchedOutages());
        }}
      >
        <img src={BackIcon} alt="Cancel" />
        <span className="close-button-label">BACK</span>
      </button>
    </div>
  );
};

const renderDrawer = (
  searchMode: ISearchModeState,
  adminMode: IAdminModeState,
  auth: IAuthState,
  onClose: {
    (event: ClickEvent): void;
    (event: ClickEvent): void;
    (event: ClickEvent): void;
  }
) => {
  if (searchMode.enabled) {
    return (
      <OUTAGE_SEARCH activeView={searchMode.activeView} onClose={onClose} />
    );
  } else if (auth.loggedIn && adminMode.enabled) {
    return <ADMIN_PANEL title="ADMIN PANEL" />;
  } else {
    return <OUTAGE_FORM onClose={onClose} />;
  }
};

const DRAWER: React.FC<IDrawerProps> = (props: IDrawerProps) => {
  const dispatch = useDispatch();
  const sendDataToGTM = useGTMDispatch();
  const { isDrawerOpen, isMobile, onClose } = props;

  const clickShieldClassname = `click-shield ${
    isDrawerOpen ? "open" : "closed"
  }`;
  const drawerContainerClassname = `drawer-container ${
    isMobile ? "mobile" : null
  }`;

  // App State
  const { submittedOutage } = useSelector((state: RootState) => state.outages);
  const { auth } = useSelector((state: RootState) => state.globalState);
  const { searchMode, adminMode, pinMode } = useSelector(
    (state: RootState) => state.globalState.ui
  );

  // Local State
  const [localDrawerState, setLocalDrawerState] = useState(false);

  useEffect(() => {
    if (isDrawerOpen !== localDrawerState) {
      setLocalDrawerState(isDrawerOpen);
      if (isDrawerOpen) {
        openDrawer(isMobile);
      } else {
        closeDrawer(isMobile, dispatch, submittedOutage, searchMode, adminMode);
      }
    }
  }, [
    adminMode,
    dispatch,
    isDrawerOpen,
    isMobile,
    localDrawerState,
    searchMode,
    submittedOutage,
  ]);

  return (
    <>
      {!isMobile && (
        <DrawerStyles.ClickSheild
          className={clickShieldClassname}
          onClick={(event) => onClose(event)}
        />
      )}

      <DrawerStyles.DrawerContainter className={drawerContainerClassname}>
        <DrawerStyles.DrawerHeader className="drawer-header">
          {searchMode.enabled &&
            searchMode.activeView !== ESearchWizardSteps.SEARCH &&
            renderBackButtonContainer(dispatch)}
          {renderTitleContainer(submittedOutage, searchMode, adminMode, auth)}
          {renderCloseButtonContainer(
            submittedOutage,
            auth,
            sendDataToGTM,
            onClose
          )}
        </DrawerStyles.DrawerHeader>

        {renderDrawer(searchMode, adminMode, auth, onClose)}
      </DrawerStyles.DrawerContainter>
    </>
  );
};

DRAWER.defaultProps = DefaultDrawerProps;

export default DRAWER;
